import { LocalStorageUtil } from '@cue/utility';

export const COOKIE_CONSENT_TYPE = 'cookieConsent';
export const OPEN_COOKIE_SETTINGS_EVENT = 'cue:open-cookie-settings';

export enum ConsentTypes {
  ALL = 'all',
  REQUIRED = 'required',
}

export type UsercentricsUi = {
  isInitialized: () => boolean;
  areAllConsentsAccepted: () => boolean;
  showSecondLayer: () => void;
};

type MatomoQueue = unknown[][];

const UC_WAIT_MS = 10000;
const LOCAL_UC_WAIT_MS = 1500;
const UC_POLL_MS = 50;
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];

const getUc = (): UsercentricsUi | undefined =>
  (window as unknown as { UC_UI?: UsercentricsUi }).UC_UI;

const pushToMatomo = (command: string) => {
  const w = window as unknown as { _paq?: MatomoQueue };
  (w._paq ||= []).push([command]);
};

/**
 * Single entry point for the cookie consent. Usercentrics is the source of
 * truth when its script is loaded. On localhost the script is domain-bound and
 * never loads, so the choice is kept in localStorage via a fallback dialog.
 */
export class Consent {
  static isLocalhost() {
    return LOCAL_HOSTS.includes(window.location.hostname);
  }

  static getStoredType() {
    return LocalStorageUtil.get<ConsentTypes>(COOKIE_CONSENT_TYPE);
  }

  /** Resolves with the Usercentrics API, or undefined if it never loads. */
  static waitForUc(): Promise<UsercentricsUi | undefined> {
    const timeout = Consent.isLocalhost() ? LOCAL_UC_WAIT_MS : UC_WAIT_MS;
    return new Promise((resolve) => {
      const startedAt = Date.now();
      const poll = () => {
        const uc = getUc();
        if (uc) return resolve(uc);
        if (Date.now() - startedAt >= timeout) return resolve(undefined);
        setTimeout(poll, UC_POLL_MS);
      };
      poll();
    });
  }

  static async areAllConsentsAccepted() {
    const uc = await Consent.waitForUc();
    if (uc) return Boolean(uc.areAllConsentsAccepted());
    return Consent.getStoredType() === ConsentTypes.ALL;
  }

  static async openPreferences() {
    const uc = await Consent.waitForUc();
    if (uc) {
      uc.showSecondLayer();
      return;
    }
    window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
  }

  /** Stores the choice and switches Matomo tracking on or off accordingly. */
  static save(type: ConsentTypes) {
    LocalStorageUtil.set<ConsentTypes>(COOKIE_CONSENT_TYPE, type);
    Consent.applyTracking(type === ConsentTypes.ALL);
  }

  static applyTracking(allAccepted: boolean) {
    pushToMatomo(allAccepted ? 'setConsentGiven' : 'forgetConsentGiven');
  }
}
